/**
 * Bots — fleet view. Lists every registered bot (hostname, version,
 * live account count, last-seen) and mints enroll codes for new ones.
 *
 * The Add Bot dialog does the mint + show-one-liner flow; this page
 * polls listBots() every 5s while the dialog is open so a newly
 * provisioned host lands in the table without a refresh and the
 * dialog auto-closes.
 */

import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { motion } from "motion/react";
import { PageHeader } from "@/components/page-header";
import { DashboardSection } from "@/components/dashboard-section";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { CoolIcon } from "@/components/ui/cool-icon";
import {
  DataTableShell,
  DataTableHeader,
  DataTableRow,
  DataTableCell,
} from "@/components/ui/data-table";
import { Loader2, Plus, XCircle } from "@/components/ui/icons";
import { AddBotDialog } from "@/components/bots/add-bot-dialog";
import { listBots, retireBot, setBotCapOverride, type BotRow } from "@/lib/api/bots";
import { toast } from "sonner";
import { extractApiErrorMessage } from "@/lib/api";
import { useConfirm } from "@/components/ui/dialog-imperative";

function fmtRelative(ts: number | null): string {
  if (!ts) return "never";
  const diff = Date.now() - ts;
  if (diff < 60_000) return "just now";
  if (diff < 3_600_000) return `${Math.floor(diff / 60_000)}m ago`;
  if (diff < 86_400_000) return `${Math.floor(diff / 3_600_000)}h ago`;
  return `${Math.floor(diff / 86_400_000)}d ago`;
}

function isLive(lastSeen: number | null): boolean {
  // 60s sync bumps last_seen; give 2 min grace before declaring a bot
  // stale (matches the 30-min sync interval as a safety margin).
  if (!lastSeen) return false;
  return Date.now() - lastSeen < 120_000;
}

export function BotsPage(): ReactNode {
  const confirm = useConfirm();
  const [rows, setRows] = useState<BotRow[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  /** Timestamp taken when the dialog opened. When listBots() returns a
   *  bot with registered_at > dialogOpenedAt, we treat it as the newly
   *  provisioned one and flip newBotRegistered so the dialog closes. */
  const dialogOpenedAt = useRef<number>(0);
  const [newBotRegistered, setNewBotRegistered] = useState(false);

  // Baseline + poll.
  useEffect(() => {
    let cancelled = false;
    const tick = async (): Promise<void> => {
      try {
        const res = await listBots();
        if (cancelled) return;
        setRows(res);
        setLoadError(null);

        // Detect "a fresh bot registered since the dialog opened" by
        // comparing registered_at against the open timestamp.
        if (dialogOpen && dialogOpenedAt.current > 0) {
          const fresh = res.some((b) => b.registered_at > dialogOpenedAt.current);
          if (fresh) setNewBotRegistered(true);
        }
      } catch (e) {
        if (cancelled) return;
        setLoadError(e instanceof Error ? e.message : String(e));
        setRows((prev) => prev ?? []);
      }
    };
    void tick();
    // 5 s cadence while the dialog is open (operator is watching), 30 s
    // otherwise — the fleet changes rarely outside of provisioning.
    const id = setInterval(tick, dialogOpen ? 5000 : 30_000);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [dialogOpen]);

  const handleOpenDialog = (): void => {
    dialogOpenedAt.current = Date.now();
    setNewBotRegistered(false);
    setDialogOpen(true);
  };

  const handleCapBlur = async (
    bot: BotRow,
    raw: string,
  ): Promise<void> => {
    const trimmed = raw.trim();
    let next: number | null = null;
    if (trimmed !== "") {
      const n = parseInt(trimmed, 10);
      if (!Number.isFinite(n) || n < 1 || n > 64) {
        toast.error("Cap override must be 1–64 (blank to use auto)");
        return;
      }
      next = n;
    }
    if (next === bot.max_concurrent_awake_override) return;
    try {
      await setBotCapOverride(bot.id, next);
      toast.success(
        next == null
          ? `${bot.id} cap → auto`
          : `${bot.id} cap → ${next} concurrent`,
      );
      setRows((prev) =>
        prev
          ? prev.map((b) =>
              b.id === bot.id
                ? { ...b, max_concurrent_awake_override: next }
                : b,
            )
          : prev,
      );
    } catch (e) {
      toast.error(extractApiErrorMessage(e));
    }
  };

  const handleRetire = async (id: string): Promise<void> => {
    const ok = await confirm({
      title: `Retire Bot "${id}"?`,
      description: "This revokes the token and the container will exit.",
      confirmText: "Retire",
      destructive: true,
    });
    if (!ok) return;
    try {
      await retireBot(id);
      toast.success(`Retired ${id}`);
      setRows((prev) =>
        prev ? prev.map((b) => (b.id === id ? { ...b, status: "retired" } : b)) : prev,
      );
    } catch (e) {
      toast.error(`Retire failed: ${e instanceof Error ? e.message : String(e)}`);
    }
  };

  const { activeCount, totalCount } = useMemo(() => {
    const list = rows ?? [];
    return {
      activeCount: list.filter((b) => b.status === "active").length,
      totalCount: list.length,
    };
  }, [rows]);

  return (
    <div className="flex flex-col gap-5">
      <PageHeader
        title="Bot Fleet — Plug And Play"
        subtitle={
          rows === null
            ? "Loading Hosts…"
            : `${activeCount} Active · ${totalCount} Total Enrolled`
        }
        icon={<CoolIcon name="Desktop_Tower" />}
        accentWords={["Play"]}
        right={
          <Button onClick={handleOpenDialog}>
            <Plus /> Add Bot
          </Button>
        }
      />

      <DashboardSection>
        {rows === null ? (
          <div className="flex flex-col items-center justify-center py-16 text-center">
            <Loader2 className="mb-3 size-5 animate-spin text-accent" />
            <p className="text-sm text-muted-foreground">Loading Hosts…</p>
          </div>
        ) : loadError && rows.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-center">
            <p className="text-sm text-destructive">Couldn't load bots: {loadError}</p>
          </div>
        ) : rows.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-center">
            <div className="mb-4 flex size-12 items-center justify-center rounded-full bg-muted">
              <CoolIcon name="Desktop_Tower" />
            </div>
            <p className="mb-1 text-sm text-foreground">No Bot Hosts Yet</p>
            <p className="mb-4 max-w-sm text-xs text-muted-foreground">
              Mint An Enroll Code, Paste The Installer On Any Linux Host, And It Self-Registers.
            </p>
            <Button onClick={handleOpenDialog}>
              <Plus /> Add Your First Bot
            </Button>
          </div>
        ) : (
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
          >
            <DataTableShell minWidth={820}>
              <DataTableHeader
                labels={["Bot", "Host", "Accounts", "Cap", "Version", "Last Seen", "Status", "Actions"]}
              />
              <tbody>
                {rows.map((b, i) => {
                  const live = isLive(b.last_seen) && b.status === "active";
                  return (
                    <DataTableRow key={b.id} index={i}>
                      <DataTableCell align="left" className="px-4 font-mono text-xs">
                        {b.id}
                      </DataTableCell>
                      <DataTableCell className="text-xs text-muted-foreground">
                        {b.hostname ?? <span className="italic">unknown</span>}
                      </DataTableCell>
                      <DataTableCell className="font-mono text-sm font-semibold text-foreground">
                        {b.assigned_account_count}
                      </DataTableCell>
                      <DataTableCell className="px-2">
                        <div className="flex flex-col items-center gap-0.5">
                          <input
                            type="text"
                            inputMode="numeric"
                            defaultValue={
                              b.max_concurrent_awake_override == null
                                ? ""
                                : String(b.max_concurrent_awake_override)
                            }
                            placeholder={
                              b.max_concurrent_awake == null
                                ? "auto"
                                : String(b.max_concurrent_awake)
                            }
                            onBlur={(e) =>
                              void handleCapBlur(b, e.target.value)
                            }
                            onKeyDown={(e) => {
                              if (e.key === "Enter") {
                                e.preventDefault();
                                (e.target as HTMLInputElement).blur();
                              }
                            }}
                            className="h-6 w-12 rounded border border-border bg-background px-1.5 text-center font-mono text-[11px] tabular-nums focus:border-accent focus:outline-none"
                            title={
                              b.reported_total_mem_mb != null
                                ? `Auto cap from ${b.reported_total_mem_mb} MB total RAM`
                                : "Awaiting hello with mem stats"
                            }
                          />
                          {b.reported_total_mem_mb != null && (
                            <span className="text-[9px] text-muted-foreground">
                              {Math.round(b.reported_total_mem_mb / 1024) > 0
                                ? `${(b.reported_total_mem_mb / 1024).toFixed(1)}G`
                                : `${b.reported_total_mem_mb}M`}
                            </span>
                          )}
                        </div>
                      </DataTableCell>
                      <DataTableCell className="font-mono text-xs text-muted-foreground">
                        {b.version ?? "—"}
                      </DataTableCell>
                      <DataTableCell className="text-xs text-muted-foreground">
                        {fmtRelative(b.last_seen)}
                      </DataTableCell>
                      <DataTableCell>
                        <Badge
                          variant={
                            b.status === "retired"
                              ? "breached"
                              : live
                                ? "passed"
                                : "warning"
                          }
                          size="xs"
                        >
                          {b.status === "retired" ? "Retired" : live ? "Live" : "Stale"}
                        </Badge>
                      </DataTableCell>
                      <DataTableCell align="right" className="px-4">
                        {b.status === "active" ? (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => void handleRetire(b.id)}
                          >
                            <XCircle /> Retire
                          </Button>
                        ) : (
                          <span className="text-xs text-muted-foreground">—</span>
                        )}
                      </DataTableCell>
                    </DataTableRow>
                  );
                })}
              </tbody>
            </DataTableShell>

            {/* Mobile cards (DataTableShell hides below lg) */}
            <ul className="flex flex-col gap-3 lg:hidden">
              {rows.map((b) => {
                const live = isLive(b.last_seen) && b.status === "active";
                return (
                  <li
                    key={b.id}
                    className="flex flex-col gap-2 rounded-xl border border-border/40 bg-card p-3 text-xs"
                  >
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <span className="truncate font-mono text-xs text-foreground">{b.id}</span>
                      <Badge
                        variant={
                          b.status === "retired"
                            ? "breached"
                            : live
                              ? "passed"
                              : "warning"
                        }
                        size="xs"
                      >
                        {b.status === "retired" ? "Retired" : live ? "Live" : "Stale"}
                      </Badge>
                    </div>
                    <div className="grid grid-cols-2 gap-2 text-[11px]">
                      <div>
                        <span className="block text-[10px] uppercase tracking-wider text-muted-foreground">Host</span>
                        <span className="truncate text-foreground">
                          {b.hostname ?? <span className="italic text-muted-foreground">unknown</span>}
                        </span>
                      </div>
                      <div>
                        <span className="block text-[10px] uppercase tracking-wider text-muted-foreground">Accounts</span>
                        <span className="font-mono font-semibold text-foreground">{b.assigned_account_count}</span>
                      </div>
                      <div>
                        <span className="block text-[10px] uppercase tracking-wider text-muted-foreground">Version</span>
                        <span className="font-mono text-muted-foreground">{b.version ?? "—"}</span>
                      </div>
                      <div>
                        <span className="block text-[10px] uppercase tracking-wider text-muted-foreground">Last Seen</span>
                        <span className="text-muted-foreground">{fmtRelative(b.last_seen)}</span>
                      </div>
                    </div>
                    {b.status === "active" && (
                      <Button
                        variant="outline"
                        size="sm"
                        className="self-end"
                        onClick={() => void handleRetire(b.id)}
                      >
                        <XCircle /> Retire
                      </Button>
                    )}
                  </li>
                );
              })}
            </ul>
          </motion.div>
        )}
      </DashboardSection>

      <AddBotDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        newBotRegistered={newBotRegistered}
      />
    </div>
  );
}
